import React, { useState, useEffect, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { PostCreator } from './PostCreator';
import { useAuth } from '../contexts/AuthContext';
import { Community, UserData, Attachment, PostType, Post, ShareTarget, Achievement, DailyAchievements } from '../types';
import { sendChannelMessage, startConversationOnServer, addMessageToConversationOnServer } from '../api';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, PlusIcon, SendIcon } from './Icons';
import { FootprintArchiveModal } from './FootprintPage';

interface GlobalPostModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreatePost: (data: any) => any;
    communities: Community[];
    conversations: any[];
    postBlurOpacity: number;
    achievements: Achievement[];
    dailyAchievements: DailyAchievements;
    initialMedia?: string;
    initialDestination?: 'feed' | 'community' | 'footprint';
}

export const GlobalPostModal: React.FC<GlobalPostModalProps> = ({
    isOpen,
    onClose,
    onCreatePost,
    communities,
    conversations,
    postBlurOpacity,
    achievements,
    dailyAchievements,
    initialMedia,
    initialDestination
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const [destination, setDestination] = useState<'feed' | 'community' | 'footprint'>(initialDestination || 'feed');
    const [selectedCommunityId, setSelectedCommunityId] = useState<string>('');
    const [selectedChannelId, setSelectedChannelId] = useState<string>('posts');
    const [shareToUsers, setShareToUsers] = useState<string[]>([]);
    
    // We can allow searching users
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedArchivedTaskId, setSelectedArchivedTaskId] = useState<number | null>(null);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

    const dailyTask = dailyAchievements?.daily;
    const archivedTasks = achievements.filter(ach => ach.isArchived && (!dailyTask || ach.id !== dailyTask.id));

    useEffect(() => {
        if (isOpen) {
            setDestination(initialDestination || 'feed');
            setSelectedCommunityId('');
            setSelectedChannelId('posts');
            setShareToUsers([]);
            setSearchQuery('');
            setSelectedArchivedTaskId(null);
        }
    }, [isOpen, initialDestination]);

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };
    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const joinedCommunities = communities.filter(c => c.members?.includes(currentUser || '') || c.admins?.includes(currentUser || '') || c.creator === currentUser);

    const availableUsers = useMemo(() => {
        const friends = currentUserData?.friends || [];
        const following = currentUserData?.following || [];
        const followers = currentUserData?.followers || [];
        const uniqueSet = new Set([...friends, ...following, ...followers]);
        
        return Array.from(uniqueSet).map(userId => ({
            id: userId,
            ...usersData[userId]
        })).filter(u => u.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) || u.id.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [currentUserData, usersData, searchQuery]);

    const handleCreatePost = async (postData: any) => {
        let finalData = { ...postData };
        if (destination === 'community') {
            if (!selectedCommunityId) {
                alert('Please select a community');
                return;
            }
            finalData.communityId = selectedCommunityId;
            finalData.channelId = selectedChannelId;
        } else if (destination === 'footprint') {
            const targetTaskId = selectedArchivedTaskId || dailyTask?.id;
            const completedTasks = currentUserData?.completedAchievements || [];
            const isAlreadyCompleted = targetTaskId && completedTasks.some((c: any) => Number(c.achievementId) === Number(targetTaskId));
            if (isAlreadyCompleted) {
                alert('You have already completed this footprint task!');
                return;
            }

            finalData.isFootprintPost = true;
            if (selectedArchivedTaskId) {
                const ach = archivedTasks.find(t => t.id === selectedArchivedTaskId);
                finalData.footprintDetails = {
                    achievementId: selectedArchivedTaskId,
                    achievementTitle: ach?.title || '',
                    achievementDifficulty: ach?.difficulty || 'Easy',
                    isTimely: false
                };
            } else if (dailyTask) {
                finalData.footprintDetails = {
                    achievementId: dailyTask.id,
                    achievementTitle: dailyTask.title,
                    achievementDifficulty: dailyTask.difficulty || 'Medium',
                    isTimely: true
                };
            }
        }

        const newPost = await onCreatePost(finalData);

        // Share if needed
        if (newPost && shareToUsers.length > 0 && currentUser) {
            try {
                for (const targetId of shareToUsers) {
                    const existing = conversations.find(c => !c.isYipYap && c.participants.includes(targetId));
                    let resolvedConvoId = existing?.id;
                    if (!resolvedConvoId) {
                        const conversation = await startConversationOnServer([currentUser, targetId]);
                        resolvedConvoId = conversation.id;
                    }
                    if (resolvedConvoId) {
                        const firstProfileBgSlide = currentUserData?.profileBgSlideshow?.enabled && currentUserData.profileBgSlideshow.attachments?.length > 0
                            ? currentUserData.profileBgSlideshow.attachments[0].url
                            : currentUserData?.profileBgImage?.url || currentUserData?.profileBg?.url;

                        await addMessageToConversationOnServer(resolvedConvoId, {
                            authorId: currentUser,
                            text: '',
                            media: [],
                            sharedPostId: newPost.id,
                            sharedPostBgUrl: newPost.type === 'text' ? firstProfileBgSlide : undefined
                        });
                    }
                }
            } catch (err) {
                console.error("Failed to share post to users", err);
            }
        }
        
        onClose();
        return newPost;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center animate-fade-in p-0 md:p-8 messages-page-no-swipe" onClick={onClose}>
            <div className="absolute inset-0 bg-black/80" style={{ backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)' }} />
            <div 
                className="relative w-full h-full md:h-[85vh] max-h-full md:max-w-3xl lg:max-w-5xl flex flex-col overflow-hidden shadow-2xl border border-white/20 md:rounded-[2.5rem]"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={e => e.stopPropagation()}
            >
                <div 
                    className="p-4 flex justify-between items-center shrink-0"
                    style={{ paddingTop: Capacitor.isNativePlatform() ? 'calc(1.5rem + env(safe-area-inset-top, 0px))' : '1rem' }}
                >
                    <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Create Post</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                        {/* Left Column: Destination, Community/Footprint options & PostComposer */}
                        <div className="md:col-span-6 flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Destination</label>
                                <div className="flex gap-2">
                                    {['feed', 'community', 'footprint'].map(dest => (
                                        <button
                                            key={dest}
                                            onClick={() => setDestination(dest as any)}
                                            className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors border ${destination === dest ? 'bg-white/20 border-white/40 shadow-inner' : 'bg-black/20 border-transparent hover:bg-black/30'}`}
                                        >
                                            {dest}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {destination === 'community' && (
                                <div className="flex flex-col gap-2 animate-fade-in">
                                    <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Select Community</label>
                                    {joinedCommunities.length === 0 ? (
                                        <p className="text-xs opacity-70">You are not in any communities yet.</p>
                                    ) : (
                                        <select 
                                            className="bg-black/30 border border-white/20 rounded-2xl p-3 text-sm focus:outline-none focus:border-white/40"
                                            value={selectedCommunityId}
                                            onChange={(e) => setSelectedCommunityId(e.target.value)}
                                        >
                                            <option value="">Choose...</option>
                                            {joinedCommunities.map(c => (
                                                <option key={c._id} value={c._id}>{c.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}

                            {destination === 'footprint' && (
                                <div className="flex flex-col gap-4 animate-fade-in">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Footprint Task</label>
                                        {selectedArchivedTaskId && (
                                            <button 
                                                onClick={() => setSelectedArchivedTaskId(null)}
                                                className="text-xs text-blue-400 hover:underline px-2"
                                            >
                                                Use Daily
                                            </button>
                                        )}
                                    </div>
                                    
                                     {(() => {
                                        const activeTask = selectedArchivedTaskId ? archivedTasks.find(t => t.id === selectedArchivedTaskId) : dailyTask;
                                        const isDaily = !selectedArchivedTaskId && !!dailyTask;
                                        if (!activeTask) return <div className="text-xs opacity-70 p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                                            {selectedArchivedTaskId ? 'Selected task not found.' : 'No daily task active. Browse archived tasks below to select one.'}
                                        </div>;

                                        const completed = currentUserData?.completedAchievements || [];
                                        const isAlreadyDone = completed.some((c: any) => Number(c.achievementId) === Number(activeTask.id));

                                        return (
                                            <div className={`w-full flex flex-col gap-2 p-3 rounded-xl border shadow-inner ${isAlreadyDone ? 'bg-red-500/10 border-red-500/30' : 'bg-blue-600/25 border-blue-500/50'}`}>
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="flex-grow min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-white text-base truncate">{activeTask.title}</span>
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isDaily ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                                                                {isDaily ? 'DAILY TASK' : 'ARCHIVED TASK'}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-white/70 mt-1 line-clamp-2">{activeTask.description}</div>
                                                    </div>
                                                    <div className="flex-shrink-0 text-sm font-bold text-blue-400">
                                                        +{isDaily ? activeTask.points : Math.round(activeTask.points * 0.2)} pts
                                                    </div>
                                                </div>
                                                {isAlreadyDone && (
                                                    <div className="text-[11px] font-bold text-red-400 bg-red-500/20 border border-red-500/30 rounded-lg px-2.5 py-1 text-center">
                                                        ⚠️ You have already completed this task!
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <button 
                                        onClick={() => setIsArchiveModalOpen(true)}
                                        className="w-full py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-sm font-bold"
                                    >
                                        Browse Archived Tasks
                                    </button>
                                </div>
                            )}

                            <div className="w-full h-px bg-white/10 my-1" />

                            <div className="w-full">
                                <PostCreator
                                    onCreatePost={handleCreatePost}
                                    postBlurOpacity={postBlurOpacity}
                                    themeColor={userThemeColor}
                                    isFootprintPost={destination === 'footprint'}
                                    isCommunityPost={destination === 'community'}
                                    onCancel={onClose}
                                    initialSharedContent={initialMedia ? { text: '', media: [{ type: 'image', url: initialMedia, name: 'art.png', size: 0, file: null as any }] } : null}
                                />
                            </div>
                        </div>

                        {/* Right Column: Direct Message Sharing */}
                        <div className="md:col-span-6 flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-bold opacity-80" style={stickerTextStyle(0.18)}>Share via Direct Message (Optional)</label>
                                <input
                                    type="text"
                                    placeholder="Search friends/followers..."
                                    className="bg-black/30 border border-white/20 rounded-2xl p-2 text-sm focus:outline-none focus:border-white/40"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <div 
                                    className="flex flex-wrap gap-2 mt-2 max-h-64 md:max-h-[320px] overflow-y-auto p-1 custom-scrollbar allow-scroll overscroll-contain"
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onTouchMove={(e) => e.stopPropagation()}
                                >
                                    {availableUsers.map(u => {
                                        const isSelected = shareToUsers.includes(u.id);
                                        return (
                                            <button
                                                key={u.id}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setShareToUsers(prev => prev.filter(id => id !== u.id));
                                                    } else {
                                                        setShareToUsers(prev => [...prev, u.id]);
                                                    }
                                                }}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                            >
                                                {u.pfp ? (
                                                    <img src={u.pfp} className="w-5 h-5 rounded-full object-cover" alt="" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-[10px] font-bold">
                                                        {u.id[0]?.toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="truncate max-w-[100px]">{u.displayName || u.id}</span>
                                            </button>
                                        );
                                    })}
                                    {availableUsers.length === 0 && <span className="text-xs opacity-50 italic">No matches found.</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FootprintArchiveModal
                isOpen={isArchiveModalOpen}
                onClose={() => setIsArchiveModalOpen(false)}
                dailyAchievements={dailyAchievements}
                onNavigateToAchievement={(id) => {
                    setSelectedArchivedTaskId(id);
                    setIsArchiveModalOpen(false);
                }}
            />
        </div>
    );
};
